import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { CustomerStateFacade, StoreErrorHandler } from '@zero/shared/util-global-state';
import { AuthPayloadTicket, AuthorizationData, Ticket, TicketData } from '@zero/shared/util-models';
import { catchError, filter, finalize, map, of, switchMap, take, takeUntil, takeWhile, tap, timer } from 'rxjs';
import { TerminateDepotInfrastructure } from '../../infrastructure/terminate-depot.infrastructure';
import { terminateDepotActions, terminateDepotApiActions } from './terminate-depot.actions';
import { terminateDepotFeature } from './terminate-depot.reducers';

@Injectable({ providedIn: 'root' })
export class TerminateDepotEffects {
  readonly #actions = inject(Actions);
  readonly #store = inject(Store);
  readonly #terminateDepotInfrastructure = inject(TerminateDepotInfrastructure);
  readonly #customerStateFacade = inject(CustomerStateFacade);
  readonly #storeErrorHandler = inject(StoreErrorHandler);

  public loadChecklist$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(terminateDepotActions.loadChecklistState),
      switchMap(() =>
        this.#terminateDepotInfrastructure.getChecklist().pipe(
          map((checklist) => terminateDepotApiActions.loadChecklistStateSuccess({ checklist })),
          catchError((error) => of(terminateDepotApiActions.loadChecklistStateFailure({ error }))),
        ),
      ),
    );
  });

  public terminate$ = createEffect(() => {
    return this.#actions.pipe(
      ofType(terminateDepotActions.terminate),
      tap(() => this.#customerStateFacade.loadCustomerBaseData()),
      switchMap(({ reason }) =>
        this.#customerStateFacade.customerBaseData$.pipe(
          filter((customerBaseData) => !!customerBaseData),
          map((customerBaseData) => customerBaseData.customerId),
          take(1),
          map((customerId) => {
            const ticket = new Ticket();
            ticket.customerId = customerId;
            ticket.type = 'TERMINATION';
            ticket.ticketData = new TicketData();
            ticket.ticketData.additionalProperties = {};
            ticket.ticketData.additionalProperties[TicketData.ATTR_TERMINATION_REASON] = reason;
            return new AuthorizationData<AuthPayloadTicket>(new AuthPayloadTicket(ticket));
          }),
          map((dataToAuthorize) => terminateDepotActions.setDataToAuthorize({ dataToAuthorize })),
        ),
      ),
    );
  });

  public checkStatus$ = createEffect(() => {
    const interval = 2000;
    const maxTime = 1 * 60 * 1000; // 1 minute

    return this.#actions.pipe(
      ofType(terminateDepotActions.checkStatus),
      switchMap(() =>
        timer(0, interval)
          .pipe(
            concatLatestFrom(() => this.#store.select(terminateDepotFeature.selectTerminationDone)),
            takeWhile((terminationDone) => !terminationDone),
            takeUntil(timer(maxTime)),
            finalize(() => this.#store.dispatch(terminateDepotActions.setError({ error: true }))),
          )
          .pipe(
            switchMap(() =>
              this.#terminateDepotInfrastructure.isTerminated().pipe(
                map((terminationDone) => terminateDepotApiActions.terminatedSuccess({ terminationDone })),
                catchError((error) => of(terminateDepotApiActions.terminatedFailure({ error }))),
              ),
            ),
          ),
      ),
    );
  });

  public handleBackendErrors$ = createEffect(
    () => {
      return this.#actions.pipe(
        ofType(terminateDepotApiActions.loadChecklistStateFailure),
        tap(({ error }) => this.#storeErrorHandler.handleBackendError(error)),
      );
    },
    { dispatch: false },
  );
}
